import { CARE_STAFF_ROLES, ROLE_LABEL, canSee, hasBell, isTeamRole, type Role } from '@aithora/shared';
import { useNavigate } from 'react-router-dom';
import type { Session } from '../session/store';
import { sx } from '../ui/designStyles';
import { AtlasIcon, HelpIcon, SignOutIcon } from './icons';
import { Bell } from './Bell';
import { AccountMenu } from './AccountMenu';
import { AiChip } from './AiStatus';
import { FacilitySwitcher } from './FacilitySwitcher';

const words = (name: string | null | undefined) => (name ?? '').trim().split(/\s+/).filter(Boolean);
/** "Maria Ortiz" as "M. Ortiz" (design: Care Staff · M. Ortiz). */
export function initialAndLast(name: string | null | undefined): string | null {
  const w = words(name);
  if (w.length === 0) return null;
  return w.length === 1 ? w[0] : `${w[0][0]}. ${w[w.length - 1]}`;
}
/** "Ruth Kowalski" as "Ruth K." (design: Family · Ruth K.). */
export function firstAndInitial(name: string | null | undefined): string | null {
  const w = words(name);
  if (w.length === 0) return null;
  return w.length === 1 ? w[0] : `${w[0]} ${w[w.length - 1][0]}.`;
}
/** "Eleanor Vance" as "Eleanor" (design: Atlas · Eleanor). */
export function firstName(name: string | null | undefined): string | null {
  return words(name)[0] ?? null;
}

/**
 * The role chip's text, in the design's form for each role (ROLES pill, L34926):
 * "Super Admin · Aithora", "Facility Admin · C C Young", "Care Staff · M. Ortiz",
 * "Family · Ruth K.", "Resident · Eleanor" (V7),
 * "Aithora Home · Member". The name comes from the session. While a Super Admin views as
 * another role there is no such person, so the qualifier is Aithora (who is looking).
 */
export function roleChipText(session: Session, role: Role | null = session.user.role): string {
  if (!role) return 'No console access · ' + (session.user.orgName ?? 'No facility');
  const viewing = role !== session.user.role;
  const name = viewing ? null : session.user.name;
  // V7 ROLES pill: "Resident · Eleanor".
  if (role === 'RESIDENT') return `Resident · ${firstName(name) ?? (viewing ? 'Aithora' : 'Resident')}`;
  if (role === 'MEMBER') return 'Aithora Home · Member';
  const label = ROLE_LABEL[role];
  if (isTeamRole(role) || viewing) return `${label} · Aithora`;
  if (role === 'FAMILY_MEMBER') return `${label} · ${firstAndInitial(name) ?? 'Family'}`;
  if (CARE_STAFF_ROLES.has(role)) return `${label} · ${initialAndLast(name) ?? session.user.orgName ?? 'No facility'}`;
  return `${label} · ${session.user.orgName ?? 'No facility'}`;
}

/**
 * May this role open the Atlas drawer? The roles GET /atlas/context answers (v3/atlas/
 * context.ts personaOf): Care Staff, Facility Admin, Super Admin, Operator, Partner and
 * Developer. That is exactly who may open the Atlas page.
 */
export function atlasAllowed(role: Role | null): boolean {
  return canSee(role, 'atlas');
}

/**
 * Residents and Aithora Home members talk to Atlas on their own Atlas page (/me), scoped to
 * themselves; their top-bar Atlas button opens it (Andrew, 2026-10-19: everyone gets Atlas).
 */
export function ownAtlasPage(role: Role | null): boolean {
  return role === 'RESIDENT' || role === 'MEMBER';
}

/** The design's subject chip on the Atlas button (atlasSubjectStyle, L38368). */
const SUBJECT = 'display:inline-flex;align-items:center;font-size:14px;color:#2F5C97;background:rgba(79,138,214,.13);border-radius:5px;padding:1px 7px;margin-left:2px;flex:0 0 auto;white-space:nowrap;';

export function TopBar({
  crumb,
  session,
  role,
  realRole,
  viewingAs,
  onSignOut,
  atlasOpen = false,
  onToggleAtlas,
  atlasSubject = null,
  helpOpen = false,
  onToggleHelp,
  phone = false,
  onToggleMenu,
  narrow = false,
  onDeleteAccount,
  path = '',
  wide = true,
}: {
  crumb: string;
  session: Session;
  /** The role the console renders. */
  role: Role | null;
  realRole: Role | null;
  viewingAs: Role | null;
  onSignOut: () => void;
  atlasOpen?: boolean;
  onToggleAtlas: () => void;
  /** The page's subject (a resident's name), shown on the Atlas button (design atlasSubject). */
  atlasSubject?: string | null;
  helpOpen?: boolean;
  onToggleHelp: () => void;
  /** Under 760px wide: a menu button opens the side nav as a drawer (design isPhone). */
  phone?: boolean;
  onToggleMenu?: () => void;
  /** Under 950px of console the Atlas button is icon only (design atPanelIconOnly). */
  narrow?: boolean;
  /** Opens the Delete my account dialog (the account menu's last row). */
  onDeleteAccount?: () => void;
  /** The page's path, for the switcher's note on whether this page follows it. */
  path?: string;
  /** At 1180px of console and wider the switcher shows its word and label (design scWide). */
  wide?: boolean;
}) {
  const navigate = useNavigate();
  const chip = roleChipText(session, role);
  // The design shortens the chip on a phone to the role alone (roleLabelShown).
  const chipShown = phone ? chip.split(' · ')[0] : chip;
  // The design drops the subject chip below 950px, where it would be a stub.
  const subject = !narrow && role !== 'RESIDENT' && atlasSubject ? atlasSubject : null;
  return (
    <header className={`topbar${phone ? ' topbar--phone' : ''}`}>
      {phone && onToggleMenu && (
        <button type="button" className="topbar__menu" aria-label="Menu" onClick={onToggleMenu}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
      )}
      <h1 className="topbar__crumb">{crumb}</h1>
      {/* The facility switcher (V6): Operator and Super Admin only, never on a phone. */}
      {!phone && <FacilitySwitcher wide={wide} path={path} />}
      <span className="spacer" />
      {/* The AI chip and AI status (Console V4 T183): team and building roles only. */}
      <AiChip wide={wide} />
      {/* Help for every role, as the design draws it (T182). */}
      <button type="button" className={`iconbtn${helpOpen ? ' iconbtn--on' : ''}`} aria-label="Help" title="Help" aria-expanded={helpOpen} onClick={onToggleHelp}>
        <HelpIcon />
      </button>
      {/* Atlas (T183) for every role that has it: the drawer for the roles GET /atlas/context
          answers (the roles that open the Atlas page, Family included); Residents and Aithora
          Home open their own Atlas page instead. Sales keep Atlas on its page and the phone. */}
      {ownAtlasPage(role) && !atlasAllowed(role) && (
        <button type="button" className={`atlasbtn${narrow ? ' atlasbtn--icon' : ''}`} title="Atlas" aria-label="Atlas" data-testid="atlas-own-page" onClick={() => navigate('/me')}>
          <AtlasIcon />
          {!narrow && <span>Atlas</span>}
        </button>
      )}
      {atlasAllowed(role) && (
        <button type="button" className={`atlasbtn${atlasOpen ? ' atlasbtn--on' : ''}${narrow ? ' atlasbtn--icon' : ''}`} title="Atlas" aria-label={subject ? `Atlas, about ${subject}` : 'Atlas'} aria-expanded={atlasOpen} onClick={onToggleAtlas}>
          <AtlasIcon />
          {!narrow && <span>Atlas</span>}
          {subject && <span style={sx(SUBJECT)}>{subject}</span>}
        </button>
      )}
      {/* The design's bell wrapper (T184) holds the bell, the role chip and Sign out in one
          inline-flex run with no gap between them; this keeps that spacing. */}
      <span className="topbar__cluster">
        {hasBell(role) && <Bell role={role} />}
        <AccountMenu chipText={chipShown} realRole={realRole} viewingAs={viewingAs} onDeleteAccount={onDeleteAccount} onSignOut={phone && !onToggleMenu ? onSignOut : undefined} />
        {!phone && (
          // Under 1180px of console the design draws the icon alone (tbWide), so the page
          // title keeps its room.
          <button type="button" className={`signout${wide ? '' : ' signout--icon'}`} aria-label="Sign out" title="Sign out" onClick={onSignOut}>
            <SignOutIcon />
            {wide && 'Sign out'}
          </button>
        )}
      </span>
    </header>
  );
}
